import React, { useState } from "react";
import { BiEdit, BiTrash, BiGridAlt, BiListUl } from "react-icons/bi";
import { WrittenFormat } from "../WrittenFormat";
import { formatText } from "../TruncateParties";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import { IoGrid } from "react-icons/io5";
import {FaMoneyBillWave, FaCalendarAlt, FaFileAlt, FaUser, FaTag, FaFileInvoice } from "react-icons/fa";
import { MdDescription } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaTableList } from "react-icons/fa6";
import "./style/RenderExpenses.scss";

export const RenderExpenses = ({
  expensesData,
  setModalTab,
  setShowModal,
  onEditExpense,
  onDeleteExpense,
}) => {
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    expenseId: null,
    expenseType: "",
  });
  const [viewMode, setViewMode] = useState("table");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const expenses = expensesData?.expenses || [];

  // --- Helper functions ---
  const truncateText = (text, wordLimit = 20, charLimit = 100) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= wordLimit && text.length <= charLimit) return text;

    let truncated = words.slice(0, wordLimit).join(" ");
    if (truncated.length > charLimit) {
      truncated = truncated.slice(0, charLimit);
      truncated = truncated.replace(/\s+\S*$/, "");
    }
    return truncated + "...";
  };

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter((word) => word.length > 0).length;
  };

  const getCharCount = (text) => (text ? text.length : 0);

  const toggleDescription = (expenseId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [expenseId]: !prev[expenseId],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "short",
    })} ${date.getFullYear().toString().slice(-2)}`;
  };

  // --- Event Handlers ---
  const handleDeleteClick = (expenseId, expenseType) => {
    setDeleteModal({ isOpen: true, expenseId, expenseType });
  };

  const handleConfirmDelete = async () => {
    await onDeleteExpense(deleteModal.expenseId);
    setDeleteModal({ isOpen: false, expenseId: null, expenseType: "" });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, expenseId: null, expenseType: "" });
  };

  const handleEditClick = (expense) => {
    setModalTab("Expenses");
    setShowModal(true);
    onEditExpense(expense);
  };

  const handleExpenseClick = (expenseId) => {
    navigate(`/home/expenses/viewexpense/${expenseId}`);
  };

  // --- Renderers ---
  const renderTableView = () => (
    <div className="table-responsive">
      <table className="documents-table">
        <thead>
          <tr>
            <th className="td-th-casedet">Type</th>
            <th className="td-th-casedet">Date</th>
            <th className="td-th-casedet">Amount</th>
            <th className="td-th-casedet">Resource Name</th>
            <th className="td-th-casedet">Court Ref</th>
            <th style={{ textAlign: "center" }} className="td-th-casedet">Actions</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense, index) => (
            <tr
              key={index}
              onClick={() => handleExpenseClick(expense._id)}
              style={{ cursor: "pointer" }}
              title="Click to view details"
            >
              <td className="td-th-casedet">{WrittenFormat(expense.type)}</td>
              <td className="td-th-casedet">{formatDate(expense.date)}</td>
              <td className="td-th-casedet">₹{expense.amount}</td>
              <td className="td-th-casedet">{expense.resourceName || "N/A"}</td>
              <td className="td-th-casedet">{expense.courtRef}</td>
              <td className="td-th-casedet">
                <div className="editdel-table-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(expense);
                    }}
                    className="editbtn"
                    title="Update"
                  >
                    <BiEdit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(expense._id, expense.type);
                    }}
                    className="delbtn"
                    title="Delete"
                  >
                    <BiTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderGridView = () => (
    <div className="expenses-grid-layout">
      {expenses.map((expense, index) => (
        <div
          key={index}
          className="modern-expense-card"
          onClick={() => handleExpenseClick(expense._id)}
          style={{ cursor: "pointer" }}
        >
          {/* Header Strip with Action Buttons */}
          <div className="card-header-strip">
            <div className="header-left">
              <div className="expense-type-badge">
                <FaMoneyBillWave className="type-icon" />
                {WrittenFormat(expense.type)}
              </div>
              <div className="expense-amount">₹{expense.amount}</div>
            </div>
            <div className="header-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(expense);
                }}
                className="action-btn edit-btn"
                title="Edit"
              >
                <BiEdit size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(expense._id, expense.type);
                }}
                className="action-btn delete-btn"
                title="Delete"
              >
                <BiTrash size={16} />
              </button>
            </div>
          </div>

          {/* Content Body */}
          <div className="card-content-body">
            {/* First Row - Date and Court Reference */}
            <div className="detail-row-horizontal">
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaCalendarAlt className="row-icon" />
                  Date
                </div>
                <div className="detail-value">
                  {formatDate(expense.date)}
                </div>
              </div>
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaFileInvoice className="row-icon" />
                  Court Ref
                </div>
                <div className="detail-value">{expense.courtRef}</div>
              </div>
            </div>

            {/* Second Row - Resource Name and Type of Resource */}
            <div className="detail-row-horizontal">
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaUser className="row-icon" />
                  Resource Name
                </div>
                <div className="detail-value">
                  {expense.resourceName || "N/A"}
                </div>
              </div>
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaTag className="row-icon" />
                  Type of Resource
                </div>
                <div className="detail-value">
                  {formatText(expense.typeOfResource)}
                </div>
              </div>
            </div>

            {expense.documentUrl && (
              <div className="detail-row">
                <div className="detail-label">
                  <FaFileAlt className="row-icon" />
                  Document
                </div>
                <div className="detail-value">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${expense.documentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-doc-link"
                  >
                    View Document →
                  </a>
                </div>
              </div>
            )}

            {expense.description && (
              <div className="detail-row">
                <div className="detail-label">
                  <MdDescription className="row-icon" />
                  Description
                </div>
                <div className="detail-value description-text">
                  {expandedDescriptions[expense._id]
                    ? expense.description
                    : getWordCount(expense.description) > 20 ||
                      getCharCount(expense.description) > 120
                    ? truncateText(expense.description, 20, 120)
                    : expense.description}
                  {(getWordCount(expense.description) > 20 ||
                    getCharCount(expense.description) > 120) && (
                    <button
                      className="read-more-btn"
                      onClick={(e) => toggleDescription(expense._id, e)}
                    >
                      {expandedDescriptions[expense._id]
                        ? "Show Less"
                        : "Read More"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="expenses-container">
      {/* Header Summary and Actions */}
      <div className="expenses-header summary header-actions-container">
        <div className="expenses-summary sum-amount">
          <p className="margin-zero exp-count">
            <strong>Total Expenses -</strong> {expensesData?.expenseCount}
          </p>
          <p className="margin-zero">
            <strong>Amount - </strong> ₹ {expensesData?.totalAmount}
          </p>
        </div>

        <div className="expenses-actions-right actions-right">
          {/* View Toggles - Only show if there is data */}
          {expenses.length > 0 && (
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <IoGrid />
              </button>
              <button
                className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaTableList />
              </button>
            </div>
          )}

          <div className="add-expense-btn tab-add">
            <button
              onClick={() => {
                setModalTab("Expenses");
                setShowModal(true);
                onEditExpense(null);
              }}
            >
              Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Body */}
      {expenses.length === 0 ? (
        <div className="no-data-container">
          <h3 className="no-data-title">No Expenses Found</h3>
          <p className="no-data-message">
            No expenses have been recorded for this case yet. Click "Add
            Expense" to track your first expense.
          </p>
        </div>
      ) : viewMode === "table" ? (
        renderTableView()
      ) : (
        renderGridView()
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={`this expense "${WrittenFormat(deleteModal.expenseType)}"`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};